import axiosInstance from "../utils/api";
import { normalizeRunAgentPayloadForStorage } from "../utils/runAgentResponse";

const OUTPUTS_BASE = "/api/v1/automation_outputs";

const jsonHeaders = {
  "Content-Type": "application/json",
};

const normalizeListResponse = (response) => {
  const body = response.data ?? {};
  const dataBlock =
    body.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : body;

  const outputs =
    dataBlock.automation_outputs ??
    body.automation_outputs ??
    (Array.isArray(body.data) ? body.data : null) ??
    (Array.isArray(body) ? body : []);

  const pagination = dataBlock.pagination ?? body.pagination ?? null;
  const normalizedPagination = pagination
    ? {
        current_page: pagination.current_page ?? pagination.page ?? 1,
        per_page: pagination.per_page ?? pagination.size ?? 20,
        total_pages: pagination.total_pages ?? 1,
        total_count: pagination.total_count ?? outputs.length,
      }
    : null;

  const listColumns = dataBlock.list_columns ?? body.list_columns ?? null;

  return {
    outputs: Array.isArray(outputs) ? outputs : [],
    pagination: normalizedPagination,
    listColumns: Array.isArray(listColumns) && listColumns.length > 0 ? listColumns : null,
  };
};

const normalizeRecord = (response) => {
  const body = response.data ?? {};
  return body.data ?? body.automation_output ?? body.output ?? body;
};

/** API shape: { "response": { ... } } — unwrap summary / multiple before save. */
export const normalizeFourayResponseForApi = (value) => {
  if (value == null) return { response: {} };
  if (typeof value === "object" && !Array.isArray(value) && "response" in value) {
    return {
      response: normalizeRunAgentPayloadForStorage(value.response) ?? {},
    };
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return { response: normalizeRunAgentPayloadForStorage(value) ?? {} };
  }
  return { response: {} };
};

const cleanObject = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== "")
  );
};

/** API expects output_table_name in lowercase (e.g. automation_outputs). */
export const normalizeOutputTableName = (name, fallback = "automation_outputs") => {
  const value = String(name ?? "").trim() || String(fallback ?? "").trim();
  return value.toLowerCase();
};

const buildOutputPayload = (form, { isEdit = false, defaultAgentId = null } = {}) => {
  if (isEdit) {
    const payload = {};
    if (form.agentId !== "" && form.agentId != null) {
      payload.agent_id = Number(form.agentId);
    } else if (defaultAgentId) {
      payload.agent_id = Number(defaultAgentId);
    }
    if (form.status?.trim()) {
      payload.status = form.status.trim();
    }
    if (form.metadata != null) {
      payload.metadata = cleanObject(form.metadata);
    }
    return { automation_output: payload };
  }

  const agentId =
    form.agentId !== "" && form.agentId != null
      ? Number(form.agentId)
      : defaultAgentId != null
        ? Number(defaultAgentId)
        : undefined;

  const payload = {
    agent_id: agentId,
    output_table_name: normalizeOutputTableName(form.outputTableName),
    status: form.status?.trim() || "completed",
    fouray_response: normalizeFourayResponseForApi(form.fourayResponse),
    input_json: cleanObject(form.inputJson),
    metadata: cleanObject(form.metadata),
  };

  if (form.automationId !== "" && form.automationId != null) {
    payload.automation_id = Number(form.automationId);
  }

  return { automation_output: payload };
};

/** POST body for run-agent save: { automation_output: { agent_id, output_table_name, ... } } */
export const buildRunAgentAutomationOutputPayload = ({
  agentId,
  outputTableName,
  fourayResponse,
  inputJson = {},
  metadata = {},
  status = "completed",
} = {}) => ({
  automation_output: {
    agent_id: Number(agentId),
    output_table_name: normalizeOutputTableName(outputTableName),
    status: status || "completed",
    fouray_response: normalizeFourayResponseForApi(fourayResponse),
    input_json:
      inputJson && typeof inputJson === "object" && !Array.isArray(inputJson)
        ? cleanObject(inputJson)
        : {},
    metadata:
      metadata && typeof metadata === "object" && !Array.isArray(metadata)
        ? cleanObject(metadata)
        : {},
  },
});

/** Create one automation_output after Run Agent (non-AP agents). */
export const createAutomationOutputFromRunAgent = async ({
  agentId,
  outputTableName,
  fourayResponse,
  inputJson = {},
  metadata = {},
  status = "completed",
} = {}) => {
  if (!agentId) {
    throw new Error("agent_id is required to create automation output");
  }

  const response = await axiosInstance.post(
    OUTPUTS_BASE,
    buildRunAgentAutomationOutputPayload({
      agentId,
      outputTableName,
      fourayResponse,
      inputJson,
      metadata,
      status,
    }),
    { headers: jsonHeaders }
  );
  return normalizeRecord(response);
};

export const resolveOutputTableNameFromAutomation = (automation, fallback = "automation_outputs") => {
  if (!automation) return normalizeOutputTableName("", fallback);
  if (automation.output_table_name?.trim()) {
    return normalizeOutputTableName(automation.output_table_name);
  }
  if (automation.workflow_name?.trim()) {
    return normalizeOutputTableName(
      automation.workflow_name.trim().replace(/\s+/g, "_")
    );
  }
  return normalizeOutputTableName("", fallback);
};

export const fetchAutomationOutputs = async (params = {}, config = {}) => {
  const response = await axiosInstance.get(OUTPUTS_BASE, { params, ...config });
  return normalizeListResponse(response);
};

export const fetchAutomationOutputById = async (outputId) => {
  if (!outputId) return null;
  const response = await axiosInstance.get(`${OUTPUTS_BASE}/${outputId}`);
  return normalizeRecord(response);
};

export const createAutomationOutput = async (form, defaultAgentId = null) => {
  const response = await axiosInstance.post(
    OUTPUTS_BASE,
    buildOutputPayload(form, { defaultAgentId }),
    { headers: jsonHeaders }
  );
  return normalizeRecord(response);
};

export const updateAutomationOutput = async (outputId, form, defaultAgentId = null) => {
  const response = await axiosInstance.patch(
    `${OUTPUTS_BASE}/${outputId}`,
    buildOutputPayload(form, { isEdit: true, defaultAgentId }),
    { headers: jsonHeaders }
  );
  return normalizeRecord(response);
};

export const deleteAutomationOutput = async (outputId) => {
  const response = await axiosInstance.delete(`${OUTPUTS_BASE}/${outputId}`);
  return response.data;
};
